import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { rethrowHandledHttpError } from '@core/http-interceptors/rethrow-handled-http-error';
import { ModuleService } from '@core/services/module.service';
import { Maillog } from '@shared/models/maillog';
import { UserRole } from '@shared/models/models';
import { PermissionUser } from '@shared/models/permissionUser';
import { User } from '@shared/models/user';
import { Location } from '@shared/models/location';
import { environment } from '@environments/environment';
import { NewUserStatusAndReason } from '@app/shared/translations/userStatus';
import { CalendarDay } from '@shared/models/dateTypes';
import { SubscriptionDto, withSubscriptionCalendarDays } from '@app/features/subscriptions/models/subscription.model';

import { UserLoginMethod } from '@shared/models/userLoginMethod';
import { UserSignInRule } from '@shared/models/userSignInRule';

// Endpoint root
const APIURL = environment.apiUrl;

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private http = inject(HttpClient);

  /** One shared answer per permission — see {@link canI}. */
  private readonly permissionCache = new Map<string, Observable<boolean>>();

  private readonly modules = inject(ModuleService);

  getList(
    searctText?: string,
    roles?: readonly number[],
    statusId?: readonly number[],
    start?: CalendarDay | null,
    end?: CalendarDay | null,
    expand: string[] = [],
  ): Observable<User[]> {
    let params = new HttpParams();

    // Opt-in expansions (e.g. 'locations') — only callers that need them pay the cost.
    if (expand.length > 0) {
      params = params.set('expand', expand.join(','));
    }

    if (searctText) {
      params = params.set('search', searctText);
    }

    for (const role of roles ?? []) {
      params = params.append('roles', role);
    }

    // `userStatus` for every one of them: the API binds that name only, and the second status on
    // used to go out as `status` and be ignored (#861).
    for (const status of statusId ?? []) {
      params = params.append('userStatus', status);
    }

    if (start && end) {
      params = params.set('start', start).set('end', end);
    }

    return this.http.get<User[]>(`${APIURL}/users/`, { params });
  }

  /**
   * Finds the customer to book for. Unlike {@link getList} — the users list, which a local
   * administrator sees scoped to their own anlæg — every administrator finds every customer here.
   */
  searchForBooking(searchText: string): Observable<User[]> {
    const params = new HttpParams().set('search', searchText).set('purpose', 'booking');
    return this.http.get<User[]>(`${APIURL}/users/`, { params });
  }

  /**
   * Gives an association admin or booker a private profile, so they can be booked for as a private
   * person. The API refuses anyone else.
   */
  addPrivateProfile(id: number): Observable<void> {
    return this.http.post<void>(`${APIURL}/users/${id}/private-profile`, {}).pipe(rethrowHandledHttpError());
  }

  get(id: number): Observable<User> {
    return this.http.get<User>(`${APIURL}/users/${id}`);
  }

  getRoles(id: number) {
    return this.http.get<UserRole[]>(`${APIURL}/users/${id}/roles`);
  }

  /**
   * Whether the signed-in user holds a permission
   * @remarks Shared per permission for the lifetime of the session: the answer cannot change
   * while the token does not, and the app asks the same handful of questions from the menu, the
   * guards and the dashboard widgets on every navigation — one HTTP round trip each, until now.
   *
   * A call that fails answers `false` and is dropped from the cache, so the next caller asks
   * again. That is also what makes caching safe here: `AuthInterceptor` turns a 401 or 403 into a
   * value on the success channel, and an `HttpErrorResponse` cached as the answer would read as
   * truthy — a "yes" for the rest of the session.
   * @param action Permission name, e.g. `manage_bookings`
   * @returns Observable of the answer, replayed to later subscribers
   */
  canI(action: string): Observable<boolean> {
    const cached = this.permissionCache.get(action);

    if (cached) {
      return cached;
    }

    const answer: Observable<boolean> = this.http.get(`${APIURL}/users/can_i?permission=${action}`).pipe(
      rethrowHandledHttpError(),
      map((data) => data === true),
      catchError(() => {
        // Only if it is still this answer that is cached: a `clearPermissionCache()` between
        // the request and its failure can have put a fresh, valid entry under the same key,
        // and an unconditional delete would evict that one instead.
        if (this.permissionCache.get(action) === answer) {
          this.permissionCache.delete(action);
        }

        return of(false);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.permissionCache.set(action, answer);

    return answer;
  }

  /** Forgets every cached `canI` answer, and which modules the user's anlæg have — call when the signed-in user changes. */
  clearPermissionCache(): void {
    this.permissionCache.clear();
    this.modules.clear();
  }

  sendResetPasswordEmail(email: string): Observable<void> {
    return this.http.get<void>(`${APIURL}/users/reset_password_request/?email=${encodeURIComponent(email)}`);
  }

  getPermissions(id: number): Observable<PermissionUser[]> {
    return this.http.get<PermissionUser[]>(`${APIURL}/users/${id}/permissions`);
  }

  updatePermissions(id: number, permissionUser: PermissionUser[]): Observable<void> {
    return this.http.post<void>(`${APIURL}/users/${id}/permissions`, permissionUser);
  }

  getMailLogs(id: number) {
    return this.http.get<Maillog[]>(`${APIURL}/users/${id}/maillogs`);
  }

  updateLocations(id: number, locations: number[]): Observable<void> {
    return this.http.post<void>(`${APIURL}/users/${id}/locations`, locations);
  }

  updateStatus(id: number, newStatusAndReason: NewUserStatusAndReason): Observable<void> {
    return this.http.put<void>(`${APIURL}/users/${id}/status`, newStatusAndReason);
  }

  softDelete(id: number): Observable<void> {
    return this.http.delete<void>(`${APIURL}/users/${id}/soft`);
  }

  getLocations(): Observable<Location[]> {
    return this.http.get<Location[]>(`${APIURL}/users/me/locations`);
  }

  /**
   * Saved on its own, not with Stamdata — `PUT /users/{id}` ignores residence. Why, in
   * `.claude/specs/abook-api/controllers.md`.
   *
   * @param municipalityCode a code from {@link MunicipalityService.getList}, or `null` to clear it.
   */
  updateResidence(id: number, municipalityCode: string | null): Observable<void> {
    return this.http.put<void>(`${APIURL}/users/${id}/residence`, { municipalityCode }).pipe(
      // AuthInterceptor swallows 401/403 and emits the HttpErrorResponse as a *value*, so without
      // this a refused save would run the caller's success path — updating the form's own copy of
      // the user and navigating away from a change the database never took.
      rethrowHandledHttpError(),
    );
  }

  getSubscriptions(): Observable<SubscriptionDto[]> {
    return this.http
      .get<SubscriptionDto[]>(`${APIURL}/users/me/subscriptions`)
      .pipe(map((subscriptions) => subscriptions.map(withSubscriptionCalendarDays)));
  }

  getLoginMethods(id: number): Observable<UserLoginMethod[]> {
    return this.http.get<UserLoginMethod[]>(`${APIURL}/users/${id}/login-methods`).pipe(
      // AuthInterceptor swallows 401/403 and emits the HttpErrorResponse as a *value*, so without
      // this every caller's success path runs with a non-array in hand — the Login-metoder tab
      // renders it, and the Stamdata banner's catchError never fires for the one failure it exists
      // for. Same reason as updateResidence above.
      rethrowHandledHttpError(),
    );
  }

  removeLoginMethod(userId: number, loginId: number): Observable<void> {
    return this.http.delete<void>(`${APIURL}/users/${userId}/login-methods/${loginId}`).pipe(
      // Same swallowed 401/403 as getLoginMethods above: without this a refused delete resolves as
      // a value, so onRemove reports "Login-metode fjernet" for a row the API kept.
      rethrowHandledHttpError(),
    );
  }

  getSignInRule(id: number): Observable<UserSignInRule> {
    return this.http.get<UserSignInRule>(`${APIURL}/users/${id}/sign-in-rule`).pipe(rethrowHandledHttpError());
  }

  /** Requires `manage_user_sign_in_exemption`. Rethrows, so a refused save is not reported as saved. */
  setSignInExemption(id: number, reason: string): Observable<void> {
    return this.http.put<void>(`${APIURL}/users/${id}/sign-in-exemption`, { reason }).pipe(rethrowHandledHttpError());
  }

  removeSignInExemption(id: number): Observable<void> {
    return this.http.delete<void>(`${APIURL}/users/${id}/sign-in-exemption`).pipe(rethrowHandledHttpError());
  }

}
